import { type FC, useMemo } from 'react';
import { PiArrowsDownUpLight, PiPlusLight, PiXLight } from 'react-icons/pi';
import { FilterCondition } from './filter-condition';
import { FilterValueInput } from './filter-value-input';
import Button from '../../../atoms/buttons/button';
import {
  type FilterFieldOptions,
  type Filter,
  type FilterValue,
  type UserOption,
  type ListOption,
  type StatusOption,
  type FilterOption,
} from 'src/modules/shared/types/filters.types';
import { getDefaultValueForFieldType } from 'src/modules/shared/utils/filters/values.util';
import { SearcherDocumentSearchFilterOperator } from 'src/api/generated';
import { Typography } from '@material-tailwind/react';
import { EmptyState } from '../../../atoms/states/empty-state';
import { useCustomFieldListOptions } from 'src/modules/shared/hooks/api/custom-fields/list-options';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { updateFilterValue } from 'src/modules/shared/utils/filters/updates.util';
import { updateFilterOperator } from 'src/modules/shared/utils/filters/updates.util';
import {
  ARCHIVE_STATUS_OPTIONS,
  INTERACTION_DIRECTION_OPTIONS,
} from 'src/modules/communications/types/filters.type';

export interface FilterBuilderProps {
  fieldsOptions: FilterFieldOptions[];
  filters: Filter[];
  onChange: (fields: Filter[]) => void;
  userOptions?: UserOption[];
  statusOptions?: StatusOption[];
  propertyOptions?: FilterOption[];
  propertyLoading?: boolean;
  onPropertyQueryChange?: (query: string) => void;
}

export const FilterBuilder: FC<FilterBuilderProps> = ({
  fieldsOptions,
  filters,
  onChange,
  userOptions = [],
  statusOptions = [],
  propertyOptions = [],
  propertyLoading = false,
  onPropertyQueryChange,
}) => {
  const listDefinitionIds = useMemo(() => {
    const listFields = fieldsOptions.filter(
      (field) => field.type === 'list' && field.definitionId !== undefined
    );
    const definitionIds = listFields.map((field) => field.definitionId);
    const validDefinitionIds = definitionIds.filter(
      (id): id is number => id !== undefined
    );

    return validDefinitionIds;
  }, [fieldsOptions]);

  const optionsQuery = useMemo(() => {
    if (listDefinitionIds.length === 0) return null;

    return createQuery()
      .equals('definitionId', listDefinitionIds)
      .take(100)
      .build();
  }, [listDefinitionIds]);

  const { options: listOptions } = useCustomFieldListOptions({
    ...(optionsQuery ?? {}),
    enabled: listDefinitionIds.length > 0,
  });

  const optionsMap = useMemo(() => {
    const map: Record<number, ListOption[]> = {};

    listOptions.forEach((option) => {
      const definitionOptions = (map[option.definitionId] ??= []);

      definitionOptions.push({
        definitionId: option.definitionId,
        label: option.label ?? option.value,
        value: option.value,
      });
    });

    return map;
  }, [listOptions]);

  const getOptionsForField = (
    fieldOption?: FilterFieldOptions
  ): FilterOption[] => {
    if (!fieldOption) return [];

    if (fieldOption.type === 'user') {
      return userOptions;
    }

    if (fieldOption.type === 'status') {
      return statusOptions.map((status) => ({
        id: status.id,
        label: status.name,
        value: String(status.id),
      }));
    }

    if (fieldOption.type === 'property') {
      return propertyOptions;
    }

    if (fieldOption.type === 'list') {
      if (fieldOption.id === 'archivedStatus') {
        return ARCHIVE_STATUS_OPTIONS.map((option, index) => ({
          id: index,
          value: option.value,
          label: option.label,
        }));
      }

      if (fieldOption.id === 'lastInteraction.direction') {
        const dummyDefinitionId = 0;
        return INTERACTION_DIRECTION_OPTIONS.map((option) => ({
          definitionId: dummyDefinitionId,
          value: option.value,
          label: option.label,
        }));
      }

      if (fieldOption.definitionId !== undefined) {
        return optionsMap[fieldOption.definitionId] ?? [];
      }
    }

    return [];
  };

  const handleAddFilterField = () => {
    if (fieldsOptions.length === 0) return;

    const firstField = fieldsOptions[0];
    const defaultValue = getDefaultValueForFieldType(
      firstField.type,
      getOptionsForField(firstField)
    );

    const newFilter: Filter = {
      id: `filter-${Date.now()}-${firstField.id}`,
      fieldOptionId: firstField.id,
      operator: SearcherDocumentSearchFilterOperator.EQ,
      value: defaultValue,
    };

    onChange([...filters, newFilter]);
  };

  const handleRemoveFilterField = (filterId: string) => {
    const remainingFields = filters.filter((filter) => filter.id !== filterId);
    onChange(remainingFields);
  };

  const handleFieldChange = (filterId: string, fieldOptionId: string) => {
    const fieldOption = fieldsOptions.find(
      (option) => option.id === fieldOptionId
    );
    if (!fieldOption) return;

    const defaultValue = getDefaultValueForFieldType(
      fieldOption.type,
      getOptionsForField(fieldOption)
    );

    onChange(
      filters.map((filter) => {
        if (filter.id !== filterId) return filter;

        return {
          ...filter,
          fieldOptionId: fieldOptionId,
          operator: SearcherDocumentSearchFilterOperator.EQ,
          value: defaultValue,
        };
      })
    );
  };

  const handleOperatorChange = (
    filterId: string,
    operator: SearcherDocumentSearchFilterOperator
  ) => {
    onChange(
      filters.map((filter) => {
        if (filter.id !== filterId) return filter;

        const fieldOption = fieldsOptions.find(
          (option) => option.id === filter.fieldOptionId
        );

        return updateFilterOperator(
          filter,
          operator,
          getOptionsForField,
          fieldOption
        );
      })
    );
  };

  const handleValueChange = (filterId: string, value: FilterValue) => {
    onChange(
      filters
        .map((filter) => {
          if (filter.id !== filterId) return filter;
          return updateFilterValue(filter, value);
        })
        .filter((filter): filter is Filter => filter !== null)
    );
  };

  const handleToggleOr = (filterId: string) => {
    onChange(
      filters.map((filter) => {
        if (filter.id !== filterId) return filter;

        return { ...filter, isOr: !filter.isOr };
      })
    );
  };

  return (
    <div className="flex flex-col space-y-4">
      {filters.length === 0 ? (
        <EmptyState
          // TODO: check when implementing i18n
          description="Agrega un filtro para empezar a buscar"
          className="py-3"
        />
      ) : (
        <div className="space-y-2">
          {filters.map((filter, index) => {
            const fieldOption = fieldsOptions.find(
              (fieldOption) => fieldOption.id === filter.fieldOptionId
            );

            return (
              <div
                key={`${filter.id}-${filter.fieldOptionId}`}
                className="group rounded-xl border border-neutral-200 bg-white p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-left sm:w-12 sm:flex-shrink-0">
                    {index === 0 ? (
                      <Typography className="text-sm font-medium text-secondary-600">
                        Si
                      </Typography>
                    ) : (
                      <Button
                        variant={filter.isOr ? 'warning' : 'info'}
                        appearance="soft"
                        size="sm"
                        onClick={() => handleToggleOr(filter.id)}
                      >
                        <span className="flex items-center gap-1">
                          <Typography className="text-xs font-medium">
                            {filter.isOr ? 'O' : 'Y'}
                          </Typography>
                          <PiArrowsDownUpLight className="w-4 h-4" />
                        </span>
                      </Button>
                    )}
                  </div>

                  <Button
                    variant="secondary"
                    appearance="ghost"
                    size="sm"
                    onClick={() => handleRemoveFilterField(filter.id)}
                    aria-label="Remove filter"
                    className="self-start"
                  >
                    <PiXLight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-3 flex flex-col gap-2 sm:grid sm:grid-cols-5 sm:items-start">
                  <FilterCondition
                    fieldsOptions={fieldsOptions}
                    selectedFieldId={filter.fieldOptionId}
                    selectedOperator={filter.operator}
                    onFieldChange={(fieldOptionId) =>
                      handleFieldChange(filter.id, fieldOptionId)
                    }
                    onOperatorChange={(operator) =>
                      handleOperatorChange(filter.id, operator)
                    }
                  />

                  <div className="w-full sm:col-span-2 sm:min-w-0">
                    <FilterValueInput
                      field={fieldOption}
                      operator={filter.operator}
                      value={filter.value}
                      onChange={(value) => handleValueChange(filter.id, value)}
                      options={getOptionsForField(fieldOption)}
                      loading={
                        fieldOption?.type === 'property'
                          ? propertyLoading
                          : false
                      }
                      onQueryChange={
                        fieldOption?.type === 'property'
                          ? onPropertyQueryChange
                          : undefined
                      }
                      clientFilter={
                        fieldOption?.type === 'property' ? false : true
                      }
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-center">
        <Button appearance="ghost" size="sm" onClick={handleAddFilterField}>
          <PiPlusLight className="w-4 h-4" />
          {/* TODO: check when implementing i18n */}
          <Typography className="text-xs font-normal">
            Agregar filtro
          </Typography>
        </Button>
      </div>
    </div>
  );
};
